import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { X, Crosshair } from "lucide-react";

interface PickerSession {
  tourId: string;
  tourTitle: string;
  targetPage: string;
  isEditing: boolean;
  editingStepId?: string;
}

interface ElementPickerContextType {
  isPickerActive: boolean;
  pickerResult: string | null;
  session: PickerSession | null;
  startPicker: (session: PickerSession) => void;
  cancelPicker: () => void;
  clearResult: () => void;
}

const ElementPickerContext = createContext<ElementPickerContextType | null>(null);

export function useElementPicker() {
  const context = useContext(ElementPickerContext);
  if (!context) {
    throw new Error("useElementPicker must be used within ElementPickerProvider");
  }
  return context;
}

const STORAGE_KEY = "element-picker-session";

export function ElementPickerProvider({ children }: { children: React.ReactNode }) {
  const [, setLocation] = useLocation();
  const [isPickerActive, setIsPickerActive] = useState(false);
  const [session, setSession] = useState<PickerSession | null>(null);
  const [pickerResult, setPickerResult] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setSession(JSON.parse(stored));
        setIsPickerActive(true);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const startPicker = useCallback((newSession: PickerSession) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
    setSession(newSession);
    setIsPickerActive(true);
    setPickerResult(null);
    setLocation(newSession.targetPage || "/");
  }, [setLocation]);

  const cancelPicker = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
    setIsPickerActive(false);
    setPickerResult(null);
    setLocation("/admin/tours");
  }, [setLocation]);

  const clearResult = useCallback(() => {
    setPickerResult(null);
    setSession(null);
  }, []);

  const completePicker = useCallback((selector: string) => {
    localStorage.removeItem(STORAGE_KEY);
    setPickerResult(selector);
    setIsPickerActive(false);
    setLocation("/admin/tours");
  }, [setLocation]);

  useEffect(() => {
    if (!isPickerActive) return;
    const styleId = 'element-picker-styles';
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `* { cursor: crosshair !important; } *:hover { outline: 2px solid hsl(var(--primary)) !important; outline-offset: 2px; }`;
    document.head.appendChild(style);
    return () => { document.getElementById(styleId)?.remove(); };
  }, [isPickerActive]);

  useEffect(() => {
    if (!isPickerActive || !session) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target || target.closest('[data-picker-overlay="true"]')) return;
      e.preventDefault();
      e.stopPropagation();

      let selector = "";
      if (target.dataset.testid) {
        selector = `[data-testid="${target.dataset.testid}"]`;
      } else if (target.id) {
        selector = `#${target.id}`;
      } else {
        const tagName = target.tagName.toLowerCase();
        const classList = Array.from(target.classList).filter(c => !c.includes(':') && c.length < 30).slice(0, 3);
        selector = classList.length > 0 ? `${tagName}.${classList.join('.')}` : tagName;
      }
      completePicker(selector);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelPicker();
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isPickerActive, session, completePicker, cancelPicker]);

  return (
    <ElementPickerContext.Provider value={{ isPickerActive, pickerResult, session, startPicker, cancelPicker, clearResult }}>
      {children}
      {isPickerActive && session && (
        <div className="fixed inset-0 z-[9999] pointer-events-none" data-picker-overlay="true">
          <div className="absolute inset-0 border-4 border-primary animate-pulse" />
          <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-auto">
            <div className="bg-primary text-primary-foreground px-6 py-3 rounded-lg shadow-2xl flex items-center gap-4">
              <Crosshair className="h-5 w-5 animate-pulse" />
              <div>
                <div className="font-semibold">Element Picker Active</div>
                <div className="text-sm opacity-90">Click any element to add it as a tour step target</div>
              </div>
              <Button size="sm" variant="secondary" onClick={cancelPicker}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </ElementPickerContext.Provider>
  );
}
